import React, { useCallback, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { cn } from '../../lib/utils';
import { Bold, Italic, List, ListOrdered, Undo, Redo, Link as LinkIcon } from 'lucide-react';
import { Button } from './button';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-muted/50 rounded-t-md">
      <Button
        type="button"
        variant={editor.isActive('bold') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className="h-8 w-8 p-0"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('italic') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className="h-8 w-8 p-0"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-border mx-1" />
      <Button
        type="button"
        variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className="h-8 w-8 p-0"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className="h-8 w-8 p-0"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-border mx-1" />
      <Button
        type="button"
        variant={editor.isActive('link') ? 'default' : 'ghost'}
        size="sm"
        onClick={addLink}
        className="h-8 w-8 p-0"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-border mx-1" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="h-8 w-8 p-0"
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="h-8 w-8 p-0"
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = 'Enter text...', 
  className,
  minHeight = '120px',
  readOnly = false 
}) => {
  const handleUpdate = useCallback(({ editor }) => {
    const html = editor.getHTML();
    // Only call onChange if content actually changed
    if (html !== value) {
      onChange(html);
    }
  }, [onChange, value]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        history: true,
      }),
      Placeholder.configure({ 
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      Link.configure({ 
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: handleUpdate,
    editorProps: {
      attributes: {
        class: 'focus:outline-none',
      },
    },
  });

  // Only update content when loading existing data (not during typing)
  useEffect(() => {
    if (editor && value && !editor.isFocused) {
      const currentContent = editor.getHTML();
      // Only set content if it's different and editor is not focused
      if (value !== currentContent && value !== '<p></p>') {
        editor.commands.setContent(value, false);
      }
    }
  }, [editor, value]);

  return (
    <div className={cn("rich-text-editor border rounded-md overflow-hidden bg-background", className)}>
      {!readOnly && <MenuBar editor={editor} />}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm dark:prose-invert max-w-none"
      />
      <style>{`
        .rich-text-editor .tiptap {
          min-height: ${minHeight};
          padding: 12px;
          outline: none;
        }
        .rich-text-editor .tiptap.ProseMirror {
          min-height: ${minHeight};
        }
        .rich-text-editor .tiptap p.is-editor-empty:first-child::before {
          color: hsl(var(--muted-foreground));
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .rich-text-editor .tiptap ul {
          list-style-type: disc !important;
          padding-left: 1.5rem !important;
          margin: 0.5rem 0 !important;
        }
        .rich-text-editor .tiptap ol {
          list-style-type: decimal !important;
          padding-left: 1.5rem !important;
          margin: 0.5rem 0 !important;
        }
        .rich-text-editor .tiptap ul li,
        .rich-text-editor .tiptap ol li {
          display: list-item !important;
          margin: 0.25rem 0;
        }
        .rich-text-editor .tiptap li p {
          margin: 0;
          display: inline;
        }
        .rich-text-editor .tiptap a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
        .rich-text-editor .tiptap p {
          margin: 0.25rem 0;
        }
        .rich-text-editor .tiptap p:first-child {
          margin-top: 0;
        }
        .rich-text-editor .tiptap:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
};

// Read-only display component for viewing rich text content
export const RichTextDisplay = ({ content, className }) => {
  if (!content || content === '<p></p>') return <span className="text-muted-foreground">-</span>;
  
  return (
    <div 
      className={cn("prose prose-sm dark:prose-invert max-w-none", className)}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

export default RichTextEditor;
